import {
  MembershipBenefitType,
  MembershipRecognitionMode,
  MembershipRecognitionScope,
} from "@/types/enums/membership";
import { MembershipsOverview } from "@/types/inventory/member-collections";
import {
  BulkMembershipUpdate,
  MembershipDetail,
  MembershipRecognitionRestampPreview,
} from "@/types/inventory/membership";
import { Page, PageParams } from "@/types/pagination";

import { requestData } from "@/api/api-config";

export async function listMemberships(
  filters?: PageParams & {
    ids?: string[];
    name?: string;
    isArchived?: boolean;
    showInCheckOut?: boolean;
    discountItemName?: string;
    categories?: string[];
  },
): Promise<Page<MembershipDetail> | null> {
  return await requestData<Page<MembershipDetail>>(
    "inventory/memberships",
    "get",
    {
      searchParams: {
        page: filters?.page,
        per_page: filters?.perPage,
        sort_by: filters?.sortBy,
        sort_direction: filters?.sortDirection,
        ids: filters?.ids,
        name: filters?.name,
        is_archived: filters?.isArchived,
        show_in_check_out: filters?.showInCheckOut,
        discount_item_name: filters?.discountItemName,
        categories: filters?.categories,
      },
    },
    false,
  );
}

export async function getMembership(
  membershipId: string,
): Promise<MembershipDetail | null> {
  return await requestData<MembershipDetail>(
    `inventory/memberships/${membershipId}`,
    "get",
    {},
    false,
  );
}

// catchError=false on both: the API answers a rejected save with the reason — a fee
// schedule carrying duplicate cycle numbers comes back a 400 saying exactly that — and
// the edit page shows it via parseError. Swallowed, the rejection resolves to null and
// every failure reads the same generic way.
export async function createMembership(
  membership: MembershipDetail,
): Promise<MembershipDetail | null> {
  return await requestData<MembershipDetail>(
    "inventory/memberships",
    "post",
    {
      json: membership,
    },
    false,
  );
}

export async function updateMembership(
  membershipId: string,
  membershipUpdate: MembershipDetail,
): Promise<MembershipDetail | null> {
  return await requestData<MembershipDetail>(
    `inventory/memberships/${membershipId}`,
    "put",
    {
      json: membershipUpdate,
    },
    false,
  );
}

export async function previewRecognitionRestamp(
  membershipId: string,
  params: {
    benefitType: MembershipBenefitType;
    recognitionMode: MembershipRecognitionMode;
    scope: MembershipRecognitionScope;
    valuedTemplateIds?: string[];
    unvaluedTemplateIds?: string[];
  },
): Promise<MembershipRecognitionRestampPreview | null> {
  return await requestData<MembershipRecognitionRestampPreview>(
    `inventory/memberships/${membershipId}/recognition-restamp-preview`,
    "get",
    {
      searchParams: {
        benefit_type: params.benefitType,
        recognition_mode: params.recognitionMode,
        scope: params.scope,
        ...(params.valuedTemplateIds?.length
          ? { valued_template_ids: params.valuedTemplateIds }
          : {}),
        ...(params.unvaluedTemplateIds?.length
          ? { unvalued_template_ids: params.unvaluedTemplateIds }
          : {}),
      },
    },
  );
}

export async function archiveMembership(
  membershipId: string,
): Promise<boolean> {
  const result = await requestData<boolean>(
    `inventory/memberships/${membershipId}`,
    "delete",
    undefined,
    false,
  );
  if (result !== true) {
    throw new Error("Failed to archive membership");
  }
  return true;
}

export async function duplicateMembership(
  membershipId: string,
): Promise<MembershipDetail | null> {
  return await requestData<MembershipDetail>(
    `inventory/memberships/${membershipId}/duplicate`,
    "post",
  );
}

export async function cancelMembership(
  memberId: string,
): Promise<boolean | null> {
  return await requestData<boolean>(
    `inventory/members/${memberId}/cancel`,
    "post",
  );
}

export async function bulkUpdateMemberships(
  updates: BulkMembershipUpdate[],
): Promise<MembershipDetail[] | null> {
  return await requestData<MembershipDetail[]>(
    "inventory/memberships/bulk",
    "put",
    { json: updates },
  );
}

export async function bulkArchiveMemberships(ids: string[]): Promise<boolean> {
  const result = await requestData<boolean>(
    "inventory/memberships/bulk-archive",
    "post",
    { json: ids },
    false,
  );
  if (result !== true) {
    throw new Error("Failed to archive memberships");
  }
  return true;
}

/** Live member counts, monthly dues and what's past due, clinic-wide and per plan. */
export async function getMembershipsOverview(): Promise<MembershipsOverview | null> {
  // Throws so an outage isn't shown as zero members and nothing past due.
  return await requestData<MembershipsOverview>(
    "inventory/memberships/overview",
    "get",
    {},
    false,
  );
}
